import React, { useRef } from 'react'
import Box from '@material-ui/core/Box'
import { StyledImagePicker } from './styledComponents'
import { UnderlinedButton } from '../Buttons/UnderlinedButton'
import { InputError, InputLabel } from '../StyledInput/styledComponents'
import { translate } from '@utils/translationUtils'

const stripMetadata = (file) =>
    new Promise((resolve) => {
        // GIF and SVG can't be reliably redrawn via canvas — pass through as-is
        if (file.type === 'image/gif' || file.type === 'image/svg+xml') {
            return resolve(file)
        }

        const url = URL.createObjectURL(file)
        const img = new Image()

        img.onload = () => {
            URL.revokeObjectURL(url)
            const canvas = document.createElement('canvas')
            canvas.width = img.naturalWidth
            canvas.height = img.naturalHeight
            canvas.getContext('2d').drawImage(img, 0, 0)
            canvas.toBlob(
                (blob) => resolve(new File([blob], file.name, { type: file.type })),
                file.type
            )
        }

        img.onerror = () => {
            URL.revokeObjectURL(url)
            resolve(file)
        }

        img.src = url
    })

export const ImagePicker = ({
    label,
    isSizeError,
    setSizeError,
    selectedImage,
    setSelectedFile,
    setSelectedImage,
    imagePickerHeight = null,
}) => {
    const filePickerRef = useRef(null)

    const triggerFilePicker = () => filePickerRef.current.click()

    const onImageChange = async ({ target }) => {
        const MAX_SIZE = 10000000
        const file = target.files[0]

        if (file) {
            setSizeError(file.size > MAX_SIZE)

            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.addEventListener('load', event => {
                setSelectedImage(event.target.result)
            })

            const cleanFile = await stripMetadata(file)
            setSelectedFile(cleanFile)
        }
    }

    const onImageDelete = () => {
        setSizeError(false)
        setSelectedFile(null)
        setSelectedImage(null)
        filePickerRef.current.value = ''
    }

    return (
        <Box marginTop={3} marginBottom={3}>
            <InputLabel>{translate(label)}</InputLabel>

            <StyledImagePicker
                src={encodeURI(selectedImage)}
                height={imagePickerHeight}
            />

            <Box display="flex" justifyContent="Space-between">
                <UnderlinedButton
                    onClick={triggerFilePicker}
                    translationKey={selectedImage ? 'replace' : 'addNew'}
                />

                {selectedImage && (
                    <UnderlinedButton
                        onClick={onImageDelete}
                        translationKey={'remove'}
                    />
                )}
            </Box>

            {isSizeError && (
                <InputError>
                    {translate('notification.maxSizeImage')}
                </InputError>
            )}

            <Box display="none">
                <input
                    ref={filePickerRef}
                    type="file"
                    onChange={onImageChange}
                    accept="image/*"
                />
            </Box>
        </Box>
    )
}
